import React, { Component, ErrorInfo, ReactNode } from "react";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { ROUTES } from '@/lib/routes';
import { recoverFromChunkLoadError } from "@/lib/chunkLoadRecovery";

interface ErrorBoundaryStrings {
  title: string;
  description: string;
  refresh: string;
  dashboard: string;
}

interface InnerProps {
  children: ReactNode;
  strings: ErrorBoundaryStrings;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundaryInner extends Component<InnerProps, State> {
  public state: State = { hasError: false };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error("ErrorBoundary caught an error", error, errorInfo);
    recoverFromChunkLoadError(error);
  }

  public render() {
    if (this.state.hasError) {
      const { strings } = this.props;
      return (
        <div
          className="min-h-screen flex items-center justify-center p-4 bg-background"
          role="main"
        >
          <Card className="max-w-md w-full p-8">
            <div
              className="flex flex-col items-center text-center"
              role="alert"
              aria-live="assertive"
            >
              <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
              </div>

              <h2 className="text-2xl font-bold mb-2">{strings.title}</h2>

              <p className="text-muted-foreground mb-6">
                {strings.description}
              </p>

              <div className="flex gap-3">
                <Button
                  onClick={() => window.location.reload()}
                  variant="default"
                >
                  {strings.refresh}
                </Button>
                <Button
                  onClick={() => (window.location.href = ROUTES.DASHBOARD)}
                  variant="outline"
                >
                  {strings.dashboard}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export function ErrorBoundary({ children }: { children: ReactNode }) {
  const { translate: t } = useTranslation();
  return (
    <ErrorBoundaryInner
      strings={{
        title: t("error_boundary_title"),
        description: t("error_boundary_description"),
        refresh: t("error_boundary_refresh"),
        dashboard: t("error_boundary_dashboard"),
      }}
    >
      {children}
    </ErrorBoundaryInner>
  );
}
